import isEmpty from 'lodash/isEmpty';

import { EN } from 'lib/locales';
import { LATEST } from 'lib/versions';
import { getComponentForApi } from 'utils/factory';

import { authHeaders, betterFetch, CCS_URL } from './helper';

/**
 * gets component data from redis
 * @param {object} params - function params
 * @param {string} params.id - the id of the component
 * @param {string} params.version -  the version of the component
 * @param {string} [params.locale = EN] - the locale of the component
 * @returns {Promise<Response>} the response object
 */
export const getComponent = async function getComponent({ id, version, locale = EN }) {
  const headers = authHeaders();
  const queryParams = new URLSearchParams({
    locale,
    version
  });
  const url = `${CCS_URL}/component/${id}?${queryParams}`;

  const response = await betterFetch(url, { headers });
  return response;
};

/**
 * gets component data from redis
 * @param {object} params - function params
 * @param {string} params.id - the id of the component
 * @param {string} [params.locale = EN] - the locale of the component
 * @returns {Promise<Response>} the response object
 */
export const getComponentLatestVersion = function getComponentLatestVersion({
  id,
  locale = EN
}) {
  return getComponent({ id, version: LATEST, locale });
};

/**
 * gets component usage data from backend
 * @param {string} componentId - the id of the component
 * @returns {Promise<Response>} the reponse object
 */
export const getComponentUsageResponse = function getComponentUsageResponse(componentId) {
  const headers = authHeaders();
  return betterFetch(`${CCS_URL}/component/${componentId}/usage`, { headers });
};

/**
 * adds a component to the canvas back-end
 * @param {object} component - component to be added
 * @returns {Promise<Response>} the response object
 */
export const addComponent = function addComponent(component) {
  if (!component) {
    return Promise.reject(new Error('Component can not null.'));
  }

  const headers = authHeaders();
  const payload = getComponentForApi(component);
  const options = {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  };
  return betterFetch(`${CCS_URL}/component`, options);
};

/**
 * deletes component data from dynamo
 * @param {string} componentId - the id of the component
 * @returns {Promise<Response>} the response object
 */
export const deleteComponent = function deleteComponent(componentId) {
  const headers = authHeaders();
  const options = {
    method: 'DELETE',
    headers
  };
  const url = `${CCS_URL}/component/${componentId}`;
  return betterFetch(url, options);
};

/**
 * update a component
 * @param {object} component - component to be added
 * @param {boolean} force - true to force update component from V1 to V2
 * (will skip checking existing v2 version)
 * @returns {Promise<Response>} the response object
 */
export const updateComponent = function updateComponent(component, force) {
  if (!component) {
    return Promise.reject(new Error('Component can not null.'));
  }

  const headers = authHeaders();
  const payload = getComponentForApi(component);

  const options = {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload)
  };
  return betterFetch(`${CCS_URL}/component?force=${force}`, options);
};

/**
 * adds a component to the canvas back-end
 * @param {string} component - the component to be published
 * @param {boolean} publishNow - flag for publishNow true/false.
 * @param {string} dateTime - dateTime of the scheduled publish
 * @param {boolean} isTestContent - published for testing purposes only
 * @returns {Promise<Response>} the response object
 */
export const publishComponent = function publishComponent(
  component,
  publishNow,
  dateTime,
  isTestContent = false
) {
  if (!component) {
    return Promise.reject(new Error('Component can not null.'));
  }
  if (!component.Id) {
    return Promise.reject(new Error('Component id can not null.'));
  }
  if (!component.Version) {
    return Promise.reject(new Error('Component version can not null.'));
  }

  const headers = authHeaders();
  const options = {
    method: 'POST',
    headers
  };

  const queryParams = new URLSearchParams({
    version: component.Version,
    locale: component.Locale,
    publishNow,
    startDate: dateTime,
    lastTimestamp: component.LastTimestamp,
    isTestContent
  });

  const url = `${CCS_URL}/component/publish/${component.Id}?${queryParams}`;

  return betterFetch(url, options);
};

/**
 * un-publishes a dynamic component in the canvas back-end
 * @param {string} component - the component to be published
 * @returns {Promise<Response>} the response object
 */
export const unpublishComponent = function unpublishComponent(component) {
  if (!component) {
    return Promise.reject(new Error('Component can not null.'));
  }
  if (!component.Id) {
    return Promise.reject(new Error('Component id can not null.'));
  }

  const headers = authHeaders();
  const options = {
    method: 'POST',
    headers
  };

  const queryParams = new URLSearchParams({
    locale: component.Locale,
    lastTimestamp: component.LastTimestamp
  });
  const url = `${CCS_URL}/component/unpublish/${component.Id}?${queryParams}`;

  return betterFetch(url, options);
};

/**
 * checks whether name of the component is available for new component or not
 * @param {string} id - of the component
 * @param {string} componentName - name of the component
 * @returns {Promise<Response>} the response object
 */
export const checkComponentNameAvailability = function checkComponentNameAvailability(
  id,
  componentName
) {
  if (isEmpty(componentName)) {
    return Promise.resolve({ nameAvailable: true });
  }

  const headers = authHeaders();
  const payload = { Id: id, ComponentName: componentName };

  const options = {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  };
  return betterFetch(`${CCS_URL}/component/checkNameAvailability`, options);
};
